/**
 * Client-side helper that attaches a Firebase ID token Bearer header.
 *
 * Used wherever we call our own API routes that require auth.
 * If the user is not signed in, the request is sent without the header
 * (so endpoints that legitimately allow anonymous access still work).
 *
 * Hydration handling — on fresh page loads (e.g. after a Toss payment
 * redirect from an external origin) Firebase Auth is still restoring
 * the session from IndexedDB when the first fetch fires, so
 * `getAuth().currentUser` is transiently null. We used to send the
 * request with an empty Authorization header in that window, which
 * made the server reject legitimate logged-in calls (see the
 * "Authorization Bearer token required for non-guest workbook confirm"
 * 401 that surfaced after auth-context's forceRefresh await widened
 * the auth-init window from ~500 ms to ~1500 ms). Now we wait up to
 * 3 s for the first onAuthStateChanged callback before deciding.
 * If the user is already present at call time this adds zero latency.
 */
import { getAuth, onAuthStateChanged, type User } from "firebase/auth";

/** Wait for the first onAuthStateChanged fire (or timeout). Firebase's
 *  first callback is authoritative — it resolves only after the SDK has
 *  determined the initial auth state (either a restored user or a
 *  definitive null), so we don't burn the full timeout for genuinely
 *  anonymous callers. Only invoked when currentUser is null at entry. */
function waitForAuthUser(timeoutMs: number): Promise<User | null> {
  return new Promise<User | null>((resolve) => {
    let resolved = false;
    const done = (u: User | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      try { unsubscribe(); } catch { /* noop */ }
      resolve(u);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    const unsubscribe = onAuthStateChanged(getAuth(), (fbUser) => done(fbUser));
  });
}

export async function getAuthHeader(forceRefresh = false): Promise<Record<string, string>> {
  let u = getAuth().currentUser;
  if (!u) {
    // Session hydration race — wait briefly for the first auth callback.
    // 3 s is comfortably above the observed 1500 ms auth-init tail while
    // still failing loud enough for a truly-unauth caller.
    u = await waitForAuthUser(3000);
  }
  if (!u) return {};
  try {
    const idToken = await u.getIdToken(forceRefresh);
    return { Authorization: `Bearer ${idToken}` };
  } catch {
    return {};
  }
}

/**
 * Fetch wrapper that:
 *   1. Attaches a Firebase ID token on the first attempt.
 *   2. On a 401 / 403 response, force-refreshes the token via
 *      `getIdToken(true)` and retries **once**.
 *
 * Rationale: custom claims (e.g. `admin=true`) set server-side via the
 * Admin SDK don't reach the client until the ID token is re-issued. If
 * the user's cached token was minted before the claim was set, every
 * admin-gated call keeps 403'ing until the token naturally expires
 * (up to ~55 min) or the user signs out and back in. Force-refresh on
 * 401/403 removes that dead window without paying refresh cost on
 * every request. Single-retry is deliberate — a second failure means
 * the caller genuinely isn't admin, not a stale token.
 */
export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const first = await fetch(input, {
    ...init,
    headers: {
      ...(await getAuthHeader()),
      ...(init.headers || {}),
    },
  });
  if (first.status !== 401 && first.status !== 403) return first;
  // 401/403 → try once with a forcibly refreshed token. If the user
  // wasn't signed in there's nothing to refresh — return original.
  const currentUser = getAuth().currentUser;
  if (!currentUser) return first;
  return fetch(input, {
    ...init,
    headers: {
      ...(await getAuthHeader(true)),
      ...(init.headers || {}),
    },
  });
}
